import React from 'react';
import { Mic2, Sparkles, Star } from 'lucide-react';
import { Button } from './ui/button';

const Hero = ({ bioData }) => {
  const scrollToShows = () => {
    document.getElementById('shows')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-pink via-burgundy/20 to-burgundy">
      {/* Animated background patterns */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-burgundy rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-bordeaux rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Spotlight circles */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-20 w-2 h-2 bg-white rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute top-40 right-40 w-1 h-1 bg-white rounded-full opacity-40 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-32 left-20 w-2 h-2 bg-white rounded-full opacity-50 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute bottom-20 right-60 w-1 h-1 bg-white rounded-full opacity-60 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
        {/* Mic icon with animation */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-white/30 rounded-full blur-xl"></div>
            <Mic2 className="relative w-16 h-16 text-burgundy drop-shadow-2xl" strokeWidth={1.5} />
            <Sparkles className="w-6 h-6 text-burgundy absolute -top-2 -right-2 animate-bounce drop-shadow-lg" />
            <Star className="w-5 h-5 text-white absolute -bottom-1 -left-2 animate-pulse" fill="currentColor" />
          </div>
        </div>

        {/* Main heading */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-playfair font-bold text-burgundy mb-6 leading-tight drop-shadow-2xl">
          {bioData.name}
        </h1>
        
        {/* Stage name subtitle */}
        <p className="text-2xl md:text-3xl text-burgundy/80 font-playfair italic mb-4">
          {bioData.stageName}
        </p>

        {/* Tagline with Italian meaning */}
        <div className="max-w-2xl mx-auto mb-8">
          <p className="text-xl md:text-2xl text-burgundy/90 italic font-light drop-shadow-lg">
            {bioData.tagline}
          </p>
        </div>

        {/* Short description */}
        <p className="text-lg md:text-xl text-gray-800 max-w-3xl mx-auto mb-12 leading-relaxed drop-shadow-md">
          London-based comedian performing in <span className="font-semibold text-burgundy">English</span> & <span className="font-semibold text-bordeaux">Italian</span>
          <br />
          From comedy clubs to corporate events – bringing laughter everywhere
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={scrollToShows}
            size="lg"
            className="bg-burgundy hover:bg-bordeaux text-white rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 hover:scale-105 shadow-2xl border-2 border-burgundy"
          >
            View Upcoming Shows
          </Button>
          <Button
            onClick={scrollToContact}
            variant="outline"
            size="lg"
            className="border-2 border-burgundy text-burgundy bg-white/80 hover:bg-burgundy hover:text-white backdrop-blur-sm rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 hover:scale-105 shadow-xl"
          >
            Book Me
          </Button>
        </div>

        {/* Credentials badges */}
        <div className="mt-16 flex flex-wrap justify-center gap-4">
          <span className="px-4 py-2 bg-white/70 backdrop-blur-md rounded-full text-sm font-mono text-burgundy border-2 border-burgundy/30 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300">
            Comedy Store
          </span>
          <span className="px-4 py-2 bg-white/70 backdrop-blur-md rounded-full text-sm font-mono text-burgundy border-2 border-burgundy/30 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300">
            Leicester Square Theatre
          </span>
          <span className="px-4 py-2 bg-white/70 backdrop-blur-md rounded-full text-sm font-mono text-burgundy border-2 border-burgundy/30 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300">
            Banana Cabaret
          </span>
          <span className="px-4 py-2 bg-white/70 backdrop-blur-md rounded-full text-sm font-mono text-burgundy border-2 border-burgundy/30 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300">
            12+ Years Tech Experience
          </span>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="#FFFFFF" fillOpacity="0.95"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
